import * as React from "react";

import type {
  BedrockCatalogModel,
  BulkOrgModelConfigAction,
  OrgModelConfigSummary,
  OrgModelConfigSummaryModel,
} from "@/shared/domain/orgs";
import {
  useBulkUpdateOrgModelConfigs,
  useSearchOrgModelConfigs,
} from "@/shared/domain/orgs/hooks";
import {
  describeModelSource,
  isRobotoManaged,
} from "@/shared/domain/orgs/orgModelConfig";
import {
  Badge,
  Dialog,
  Radio,
  RadioGroup,
  RobotoButton,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http/userFacingErrorMessage";

import styles from "./AiModels.module.css";
import { offerBlocker } from "./bedrockModelForm";

interface OrgModelsDialogProps {
  /** The org whose models are edited, as its table row showed it; `null` closes the dialog. */
  org: OrgModelConfigSummary | null;
  onClose: () => void;
  /** The catalog's chat models, which "Add a model" offers. */
  chatModels: readonly BedrockCatalogModel[];
}

/**
 * Edit one org's chat models: remove a Roboto-managed one, make any
 * switched-on model in its chat picker the default, or add a catalog model.
 * Models on the org's own connections can only be made the default here; the
 * org changes them from its AI settings. Each change is a single-org bulk
 * request applied at once, so it follows the same rules (and skip reasons) as
 * the bulk actions.
 */
export function OrgModelsDialog({
  org,
  onClose,
  chatModels,
}: OrgModelsDialogProps) {
  return (
    <Dialog
      open={org !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Dialog.Content className={styles.dialog}>
        {org !== null && (
          <OrgModelsBody
            // A fresh body per org: no earlier org's message or choice.
            key={org.org_id}
            initialRow={org}
            chatModels={chatModels}
            onClose={onClose}
          />
        )}
      </Dialog.Content>
    </Dialog>
  );
}

function OrgModelsBody({
  initialRow,
  chatModels,
  onClose,
}: {
  initialRow: OrgModelConfigSummary;
  chatModels: readonly BedrockCatalogModel[];
  onClose: () => void;
}) {
  const orgId = initialRow.org_id;
  // The org's live row: the name search also matches ids, and every bulk
  // write refetches it. Until then (or if it errors) the table's row shows.
  const liveQuery = useSearchOrgModelConfigs({ name_query: orgId }, 0, 10);
  const row =
    liveQuery.data?.find((candidate) => candidate.org_id === orgId) ??
    initialRow;

  const mutation = useBulkUpdateOrgModelConfigs();
  const [message, setMessage] = React.useState<string | null>(null);
  const [addId, setAddId] = React.useState<string | null>(null);

  const apply = (action: BulkOrgModelConfigAction, done: string) => {
    setMessage(null);
    mutation.mutate(
      { org_ids: [orgId], action },
      {
        onSuccess: (result) => {
          const skipped = result.skipped.find((s) => s.org_id === orgId);
          const note = result.notes?.find((n) => n.org_id === orgId);
          setMessage(skipped?.reason ?? note?.note ?? done);
        },
      },
    );
  };

  const addable = chatModels.filter(
    (model) => !row.models.some((m) => m.id === model.id),
  );
  const hasOwnModels = row.models.some((m) => !isRobotoManaged(m));
  // The server refuses to remove an org's last chat model, so its Remove
  // button is disabled.
  const chatModelCount = row.models.filter(isChatChoice).length;

  return (
    <div className={styles.dialogBody}>
      <Dialog.Title>Models of {row.org_name}</Dialog.Title>
      <p className={styles.note}>
        {describeModelSource(row.models, undefined)}. Changes apply at once; a
        chat on a removed model moves to the org&apos;s default on its next
        turn.
        {hasOwnModels &&
          " Models on the org's own connections can only be made the default here; the org changes them from its AI settings."}
      </p>

      <ul className={styles.orgModelList} aria-label="Models">
        {row.models.map((model) => {
          const own = !isRobotoManaged(model);
          const chatChoice = isChatChoice(model);
          const lastChatModel = chatChoice && chatModelCount === 1;
          return (
            <li key={model.id} className={styles.orgModelItem}>
              <span className={styles.orgModelLabel} title={model.model_id}>
                {model.label}
                {model.is_default && <Badge variant="label">Default</Badge>}
                {model.is_utility && <Badge variant="label">Utility</Badge>}
                {own && <Badge variant="label">Own connection</Badge>}
                {!model.in_picker && (
                  <Badge variant="label">Not in picker</Badge>
                )}
                {model.enabled === false && (
                  <Badge variant="label">Switched off</Badge>
                )}
              </span>
              <span className={styles.rowActions}>
                {!model.is_default && chatChoice && (
                  <RobotoButton
                    variant="ghost"
                    size="sm"
                    disabled={mutation.isPending}
                    aria-label={`Make ${model.label} the default`}
                    onClick={() =>
                      apply(
                        { type: "set_default_model", profile_id: model.id },
                        `${model.label} is now the default.`,
                      )
                    }
                  >
                    Make default
                  </RobotoButton>
                )}
                {!own && (
                  <RobotoButton
                    variant="ghost"
                    size="sm"
                    disabled={mutation.isPending || lastChatModel}
                    title={
                      lastChatModel
                        ? "It is the org's only chat model."
                        : undefined
                    }
                    aria-label={`Remove ${model.label}`}
                    onClick={() =>
                      apply(
                        { type: "remove_model", profile_id: model.id },
                        `Removed ${model.label}.`,
                      )
                    }
                  >
                    Remove
                  </RobotoButton>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Any org may take Roboto-managed catalog models alongside its own. */}
      {addable.length > 0 && (
        <div className={styles.orgModelAdd}>
          <RadioGroup<string>
            legend="Add a model"
            value={addId}
            onValueChange={setAddId}
          >
            {addable.map((model) => {
              const blocker = offerBlocker(model);
              return (
                <Radio<string>
                  key={model.id}
                  value={model.id}
                  label={
                    blocker === undefined
                      ? model.label
                      : `${model.label} (${blocker.replace(/\.$/, "").toLowerCase()})`
                  }
                  disabled={blocker !== undefined}
                />
              );
            })}
          </RadioGroup>
          <div>
            <RobotoButton
              variant="secondary"
              size="sm"
              disabled={addId === null || mutation.isPending}
              onClick={() => {
                if (addId !== null) {
                  const label =
                    chatModels.find((m) => m.id === addId)?.label ?? addId;
                  apply(
                    {
                      type: "add_model",
                      profile_id: addId,
                      make_default: false,
                    },
                    `Added ${label}.`,
                  );
                  setAddId(null);
                }
              }}
            >
              Add
            </RobotoButton>
          </div>
        </div>
      )}

      {message !== null && (
        <p className={styles.note} role="status">
          {message}
        </p>
      )}
      {mutation.isError && (
        <p className={styles.error} role="alert">
          {userFacingErrorMessage(mutation.error)}
        </p>
      )}
      <div className={styles.dialogActions}>
        <RobotoButton onClick={onClose}>Done</RobotoButton>
      </div>
    </div>
  );
}

/**
 * Whether members can chat with the model: switched on and in the chat
 * picker. Only such a model can be the default.
 */
function isChatChoice(model: OrgModelConfigSummaryModel): boolean {
  return model.in_picker && model.enabled !== false;
}
